import React from 'react';
import {
  Alert,
  Form,
  Input,
  Select,
  Switch,
  Button,
  Space,
  Divider,
  InputNumber,
  Typography,
  Card,
  Spin,
  Badge,
  Segmented,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Node
} from '@xyflow/react';
import { fetchTestCategories } from '../../../../../api/healthcare/Testing/TestCategories';
import {
  FlowNodeData,
  STAGE_CONFIG,
  ACTION_TYPES,
  TRIGGER_EVENTS,
  WATCHER_TYPES,
  TIME_ANCHORS,
  CONDITION_TYPES,
  CONDITION_OPERATORS,
  NUMERIC_OPERATORS,
  NOTIFICATION_CHANNELS,
  NOTIFICATION_RECIPIENTS,
  TEST_PRIORITIES,
  PRACTITIONER_TASK_TYPES,
  StageType,
} from '../../../../../types/healthcare/TreatmentPlanTypes';
import { Form as InternalForm } from '../../../../../api/formApi';
import { consultStatuses } from '../../../../../types/appointments/AppointmentTypes';
import { MedicationCatalogItem, MEDICATION_CATEGORIES, defaultDosageGuide } from '../../../../../types/healthcare/MedicationCatalogTypes';
import { COMPREHENSIVE_DATA_SOURCE_OPTIONS } from '../../../../../constants/dataSources';
import type { UserUuid } from '../../../../../types/identity';

const { Text } = Typography;
const { TextArea } = Input;

export interface BuilderData {
  forms: InternalForm[];
  practitionerForms: InternalForm[];
  appointmentTypes: Array<{ id: number; name: string; color?: string }>;
  noteTemplates: Array<{ id: number; name: string }>;
  notificationTemplates: Array<{ id: number; name: string; category?: string }>;
  treatmentPlanTemplates: Array<{ id: number; name: string }>;
  workflowTemplates: Array<{ id: number; name: string }>;
  medicationCatalog: MedicationCatalogItem[];
  practitioners: Array<{ id: UserUuid; name: string }>;
  globalResourceLinks: Array<{ id: number; name: string; description?: string }>;
  globalFileResources: Array<{ id: string; name: string; file_type?: string }>;
  customTestPackages: Array<{ id: number; name: string }>;
  dataLoading: boolean;
}

interface NodePropertiesPanelProps {
  node: Node<FlowNodeData> | null;
  onUpdate: (nodeId: string, data: Partial<FlowNodeData>) => void;
  onDelete: (nodeId: string) => void;
  builderData: BuilderData;
}

const NodePropertiesPanel: React.FC<NodePropertiesPanelProps> = ({
  node,
  onUpdate,
  onDelete,
  builderData,
}) => {
  const { data: testCategoriesRes } = useQuery({
    queryKey: ['test-categories-with-types'],
    queryFn: () => fetchTestCategories({ include_test_types: true }),
    staleTime: 5 * 60 * 1000,
  });

  const testTypeOptions = (testCategoriesRes?.data ?? []).flatMap((cat) =>
    (cat.test_types ?? cat.active_test_types ?? []).map((t) => ({
      label: t.name,
      value: t.value,
      description: cat.name,
    }))
  );

  if (!node) {
    return (
      <div style={{ padding: 24, textAlign: 'center', paddingTop: 60 }}>
        <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>⚙️</div>
        <Text type="secondary">Select a node to configure it</Text>
      </div>
    );
  }

  if (node.type === 'section_group') {
    return (
      <div style={{ padding: '16px', height: '100%', overflowY: 'auto' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16,
            paddingBottom: 12,
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: (node.data.color as string) || '#4A90D9',
              flexShrink: 0,
            }}
          />
          <Text strong style={{ fontSize: 13 }}>Section Group</Text>
        </div>

        <Form layout="vertical" size="small">
          <Form.Item label="Section Name">
            <Input
              value={(node.data.label as string) ?? ''}
              onChange={(e) => onUpdate(node.id, { label: e.target.value })}
              placeholder="Section name"
            />
          </Form.Item>

          <Form.Item label="Color">
            <input
              type="color"
              value={(node.data.color as string) || '#4A90D9'}
              onChange={(e) => onUpdate(node.id, { color: e.target.value })}
              style={{ width: '100%', height: 32, borderRadius: 6, border: '1px solid #d9d9d9', cursor: 'pointer', padding: 2 }}
            />
          </Form.Item>

          <Divider style={{ margin: '12px 0' }} />

          <Button danger block size="small" onClick={() => onDelete(node.id)}>
            Remove Section
          </Button>
        </Form>
      </div>
    );
  }

  const { data } = node;
  const cfg = STAGE_CONFIG[data.stage_type as StageType];
  const update = (patch: Partial<FlowNodeData>) => onUpdate(node.id, patch);
  const updateConfig = (patch: Record<string, any>) =>
    update({ configuration: { ...(data.configuration ?? {}), ...patch } });

  // ── Action node helpers ─────────────────────────────────────────────────

  const renderActionFields = (action: any, i: number, actions: any[]) => {
    const setActionConfig = (patch: Record<string, any>) => {
      const updated = [...actions];
      updated[i] = { ...updated[i], config: { ...(updated[i].config ?? {}), ...patch } };
      updateConfig({ actions: updated });
    };
    const acfg = action.config ?? {};

    switch (action.action_type) {
      case 'send_notification':
        return (
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <Select
              value={acfg.recipient ?? 'patient'}
              onChange={(v) => setActionConfig({
                recipient: v,
                // clear practitioner_ids when switching away from specific mode
                practitioner_ids: v === 'practitioner_specific' ? (acfg.practitioner_ids ?? undefined) : undefined,
              })}
              options={NOTIFICATION_RECIPIENTS}
              style={{ width: '100%' }}
            />
            {acfg.recipient === 'practitioner_specific' && (
              <Select
                mode="multiple"
                value={acfg.practitioner_ids ?? []}
                onChange={(v) => setActionConfig({ practitioner_ids: v })}
                options={builderData.practitioners.map((p) => ({ value: p.id, label: p.name }))}
                placeholder="Select practitioners"
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                loading={builderData.dataLoading}
                notFoundContent={builderData.dataLoading ? <Spin size="small" /> : 'No practitioners found'}
              />
            )}
            <Select
              value={acfg.template_id}
              onChange={(v) => {
                const tpl = builderData.notificationTemplates.find((t) => t.id === v);
                setActionConfig({ template_id: v, template_name: tpl?.name });
              }}
              options={builderData.notificationTemplates.map((t) => ({ value: t.id, label: t.name }))}
              placeholder="Select notification template"
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              loading={builderData.dataLoading}
              notFoundContent={builderData.dataLoading ? <Spin size="small" /> : 'No templates found'}
            />
            <Select
              value={acfg.channel ?? 'email'}
              onChange={(v) => setActionConfig({ channel: v })}
              options={NOTIFICATION_CHANNELS}
              style={{ width: '100%' }}
            />
          </Space>
        );

      case 'assign_form':
        return (
          <Select
            value={acfg.form_id}
            onChange={(v) => {
              const form = builderData.forms.find((f) => f.id === v);
              setActionConfig({ form_id: v, form_name: form?.title });
            }}
            options={builderData.forms.map((f) => ({ value: f.id, label: f.title }))}
            placeholder="Select form to assign"
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            loading={builderData.dataLoading}
            notFoundContent={builderData.dataLoading ? <Spin size="small" /> : 'No forms found'}
          />
        );

      case 'assign_medication': {
        const selected = builderData.medicationCatalog.find((m) => m.id === acfg.medication_catalog_id);
        const guides = selected?.dosage_guides ?? [];
        const pinnedGuide = guides.find((g) => g.label === acfg.dosage_guide_label) ?? defaultDosageGuide(guides);

        const handleCatalogSelect = (catalogId: number) => {
          const med = builderData.medicationCatalog.find((m) => m.id === catalogId);
          if (!med) return;
          const guide = defaultDosageGuide(med.dosage_guides);
          setActionConfig({
            medication_catalog_id:  med.id,
            medication_name:        med.name,
            dosage_guide_label:     guide?.label ?? undefined,
            dosage:                 guide?.dosage              ?? acfg.dosage         ?? '',
            frequency:              guide?.frequency           ?? acfg.frequency      ?? '',
            instructions:           guide?.instructions        ?? acfg.instructions   ?? '',
            administration_route:   guide?.administration_route ?? acfg.administration_route ?? '',
            medication_category:    med.medication_category    ?? acfg.medication_category  ?? '',
          });
        };

        const handleGuideSelect = (label: string) => {
          const guide = guides.find((g) => g.label === label);
          setActionConfig({
            dosage_guide_label:   label,
            dosage:               guide?.dosage              ?? '',
            frequency:            guide?.frequency           ?? '',
            instructions:         guide?.instructions        ?? '',
            administration_route: guide?.administration_route ?? '',
          });
        };

        return (
          <Space direction="vertical" style={{ width: '100%' }} size={6}>
            <Select
              showSearch
              value={acfg.medication_catalog_id ?? undefined}
              onChange={handleCatalogSelect}
              onClear={() =>
                setActionConfig({
                  medication_catalog_id: undefined,
                  medication_name:       '',
                  dosage_guide_label:    undefined,
                  dosage:                '',
                  frequency:             '',
                  instructions:          '',
                })
              }
              filterOption={(input, opt) =>
                String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={builderData.medicationCatalog.map((m) => ({
                value: m.id,
                label: m.generic_name ? `${m.name} (${m.generic_name})` : m.name,
              }))}
              placeholder="Search medication catalog…"
              style={{ width: '100%' }}
              loading={builderData.dataLoading}
              notFoundContent={builderData.dataLoading ? <Spin size="small" /> : 'No catalog medications found'}
              allowClear
            />

            {selected && guides.length >= 2 && (
              <div>
                <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>
                  Dosage guide
                </Text>
                <Select
                  value={pinnedGuide?.label}
                  onChange={handleGuideSelect}
                  options={guides.map((g) => ({
                    value: g.label,
                    label: (
                      <span>
                        {g.label}{g.is_default ? ' (default)' : ''}
                        {g.description && (
                          <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>
                            — {g.description}
                          </Text>
                        )}
                      </span>
                    ),
                  }))}
                  placeholder="Select dosage guide"
                  style={{ width: '100%' }}
                />
              </div>
            )}

            {selected && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {selected.high_risk && (
                  <span style={{ fontSize: 10,  color: '#cf1322', border: '1px solid #ffa39e', borderRadius: 4, padding: '1px 6px' }}>
                    ⚠ High Risk
                  </span>
                )}
                {selected.controlled_substance && (
                  <span style={{ fontSize: 10, color: '#d46b08', border: '1px solid #ffd591', borderRadius: 4, padding: '1px 6px' }}>
                    Controlled
                  </span>
                )}
                {selected.schedule && (
                  <span style={{ fontSize: 10, color: '#531dab', border: '1px solid #d3adf7', borderRadius: 4, padding: '1px 6px' }}>
                    {selected.schedule}
                  </span>
                )}
                {selected.requires_script && (
                  <span style={{ fontSize: 10, color: '#0958d9', border: '1px solid #91caff', borderRadius: 4, padding: '1px 6px' }}>
                    Script required
                  </span>
                )}
              </div>
            )}

            <div style={{  borderRadius: 6, border: '1px solid #f0f0f0', padding: '8px 10px' }}>
              <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block', marginBottom: 6 }}>
                {selected ? 'Override defaults (optional)' : 'Manual entry'}
              </Text>
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                <Input
                  value={acfg.dosage ?? ''}
                  onChange={(e) => setActionConfig({ dosage: e.target.value })}
                  placeholder={pinnedGuide?.dosage ? `Default: ${pinnedGuide.dosage}` : 'Dosage (e.g. 500mg)'}
                  size="small"
                />
                <Input
                  value={acfg.frequency ?? ''}
                  onChange={(e) => setActionConfig({ frequency: e.target.value })}
                  placeholder={pinnedGuide?.frequency ? `Default: ${pinnedGuide.frequency}` : 'Frequency (e.g. twice daily)'}
                  size="small"
                />
                <Input
                  value={acfg.instructions ?? ''}
                  onChange={(e) => setActionConfig({ instructions: e.target.value })}
                  placeholder={pinnedGuide?.instructions ? `Default: ${pinnedGuide.instructions}` : 'Instructions'}
                  size="small"
                />
              </Space>
            </div>
          </Space>
        );
      }

      case 'request_script_upload':
        return (
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <Text style={{ fontSize: 11, color: '#8c8c8c' }}>
              Marks assigned medications as awaiting script upload and optionally notifies the patient.
            </Text>
            <Select
              mode="multiple"
              value={acfg.notification_template_id ? [acfg.notification_template_id] : []}
              onChange={(v) => setActionConfig({ notification_template_id: v[v.length - 1] ?? null })}
              options={builderData.notificationTemplates.map((t) => ({ value: t.id, label: t.name }))}
              placeholder="Notification template (optional)"
              style={{ width: '100%' }}
              loading={builderData.dataLoading}
              allowClear
            />
          </Space>
        );

      case 'send_booking_link':
        return (
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <Text style={{ fontSize: 11, color: '#8c8c8c' }}>
              Emails the patient a single-use booking link that expires after the clinic's booking-link window.
            </Text>
            <Select
              value={acfg.appointment_type_id ?? undefined}
              onChange={(v) => {
                const at = builderData.appointmentTypes.find((a) => a.id === v);
                setActionConfig({ appointment_type_id: v ?? null, appointment_type_name: at?.name ?? null });
              }}
              options={builderData.appointmentTypes.map((t) => ({ value: t.id, label: t.name }))}
              placeholder="Appointment type (optional — any type)"
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              loading={builderData.dataLoading}
              allowClear
            />
            <Select
              value={acfg.notification_template_id ?? undefined}
              onChange={(v) => {
                const tpl = builderData.notificationTemplates.find((t) => t.id === v);
                setActionConfig({ notification_template_id: v ?? null, template_name: tpl?.name ?? null });
              }}
              // Only booking-link templates carry {{booking_link}}; any other template would send no link.
              options={builderData.notificationTemplates
                .filter((t) => t.category === 'appointment_booking_link_notification')
                .map((t) => ({ value: t.id, label: t.name }))}
              placeholder="Template (optional — the clinic's booking-link template)"
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              loading={builderData.dataLoading}
              allowClear
            />
          </Space>
        );

      case 'cancel_appointment':
        return (
          <Input
            value={acfg.reason ?? ''}
            onChange={(e) => setActionConfig({ reason: e.target.value })}
            placeholder="Cancellation reason"
          />
        );

      case 'dispatch_consult_job':
        return (
          <Space direction="vertical" style={{ width: '100%' }} size={6}>
            <Select
              value={acfg.workflow_template_id ?? undefined}
              onChange={(v) => {
                const tpl = builderData.workflowTemplates.find((t) => t.id === v);
                setActionConfig({ workflow_template_id: v, workflow_template_name: tpl?.name });
              }}
              options={builderData.workflowTemplates.map((t) => ({ value: t.id, label: t.name }))}
              placeholder="Select workflow template"
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              loading={builderData.dataLoading}
              notFoundContent={builderData.dataLoading ? <Spin size="small" /> : 'No workflow templates found'}
            />
            <div
              style={{
                padding: '8px 10px',
                borderRadius: 6,
                border: '1px solid #bae0ff',
                fontSize: 11,
                color: '#0958d9',
                lineHeight: 1.5,
              }}
            >
              The stage will pause until the AI guidance generation completes, then automatically continue.
            </div>
          </Space>
        );

      case 'assign_global_resource': {
        const resourceType = acfg.resource_type ?? 'link';
        return (
          <Space direction="vertical" style={{ width: '100%' }} size={6}>
            <Select
              value={resourceType}
              onChange={(v) => setActionConfig({ resource_type: v, resource_link_id: undefined, file_resource_id: undefined })}
              options={[
                { value: 'link', label: 'Resource Link' },
                { value: 'file', label: 'File Resource' },
              ]}
              style={{ width: '100%' }}
            />
            {resourceType === 'link' && (
              <Select
                value={acfg.resource_link_id ?? undefined}
                onChange={(v) => {
                  const r = builderData.globalResourceLinks.find((l) => l.id === v);
                  setActionConfig({ resource_type: 'link', resource_link_id: v, resource_name: r?.name });
                }}
                options={builderData.globalResourceLinks.map((l) => ({ value: l.id, label: l.name }))}
                placeholder="Select resource link"
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                loading={builderData.dataLoading}
                notFoundContent={builderData.dataLoading ? <Spin size="small" /> : 'No global resource links found'}
              />
            )}
            {resourceType === 'file' && (
              <Select
                value={acfg.file_resource_id ?? undefined}
                onChange={(v) => {
                  const r = builderData.globalFileResources.find((f) => f.id === v);
                  setActionConfig({ resource_type: 'file', file_resource_id: v, resource_name: r?.name });
                }}
                options={builderData.globalFileResources.map((f) => ({ value: f.id, label: f.name }))}
                placeholder="Select file resource"
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                loading={builderData.dataLoading}
                notFoundContent={builderData.dataLoading ? <Spin size="small" /> : 'No global file resources found'}
              />
            )}
          </Space>
        );
      }

      case 'create_test_order':
        return (
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <Input
              value={acfg.test_type ?? ''}
              onChange={(e) => setActionConfig({ test_type: e.target.value })}
              placeholder="Test type / category"
            />
            <Select
              value={acfg.priority ?? 'routine'}
              onChange={(v) => setActionConfig({ priority: v })}
              options={TEST_PRIORITIES}
              style={{ width: '100%' }}
            />
          </Space>
        );

      default:
        return null;
    }
  };

  // ── Condition node helpers ──────────────────────────────────────────────

  const renderConditionFields = (cond: any, i: number, conditions: any[]) => {
    const conditionObj = data.configuration?.condition ?? { operator: 'and', conditions: [] };
    const setCondField = (patch: Record<string, any>) => {
      const updated = [...conditions];
      updated[i] = { ...updated[i], ...patch };
      updateConfig({ condition: { ...conditionObj, conditions: updated } });
    };

    switch (cond.type) {
      case 'form_completed':
      case 'form_not_completed':
        return (
          <Select
            value={cond.form_id}
            onChange={(v) => {
              const form = builderData.forms.find((f) => f.id === v);
              setCondField({ form_id: v, form_name: form?.title });
            }}
            options={builderData.forms.map((f) => ({ value: f.id, label: f.title }))}
            placeholder="Select form"
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            loading={builderData.dataLoading}
            notFoundContent={builderData.dataLoading ? <Spin size="small" /> : 'No forms found'}
          />
        );

      case 'appointment_type_is':
        return (
          <Select
            value={cond.appointment_type_id}
            onChange={(v) => {
              const at = builderData.appointmentTypes.find((a) => a.id === v);
              setCondField({ appointment_type_id: v, appointment_type_name: at?.name });
            }}
            options={builderData.appointmentTypes.map((t) => ({ value: t.id, label: t.name }))}
            placeholder="Select appointment type"
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            loading={builderData.dataLoading}
          />
        );

      case 'has_active_medication':
        return (
          <Input
            value={cond.medication_name ?? ''}
            onChange={(e) => setCondField({ medication_name: e.target.value })}
            placeholder="Medication name (blank = any active)"
          />
        );

      case 'days_since_plan_start':
      case 'patient_age':
        return (
          <Space style={{ width: '100%' }}>
            <Select
              value={cond.operator ?? 'gte'}
              onChange={(v) => setCondField({ operator: v })}
              options={NUMERIC_OPERATORS}
              style={{ width: 72 }}
            />
            <InputNumber
              value={cond.value ?? 0}
              onChange={(v) => setCondField({ value: v ?? 0 })}
              min={0}
              style={{ width: 90 }}
            />
            <Text type="secondary" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
              {cond.type === 'days_since_plan_start' ? 'days' : 'yrs'}
            </Text>
          </Space>
        );

      default:
        return null;
    }
  };

  // ── Per-stage-type render ───────────────────────────────────────────────

  const renderTriggerConfig = () => {
    const triggerEvent = data.configuration?.trigger_event;
    return (
      <>
        <Form.Item label="Trigger Event">
          <Select
            value={triggerEvent}
            onChange={(v) =>
              updateConfig({
                trigger_event:               v,
                filter_appointment_type_ids: undefined,
                form_id:                     undefined,
                form_name:                   undefined,
              })
            }
            options={TRIGGER_EVENTS}
            placeholder="Select trigger event"
            style={{ width: '100%' }}
          />
        </Form.Item>

        {triggerEvent === 'appointment_created' && (
          <Form.Item label="Filter by Appointment Type" extra="Leave empty to trigger for all types">
            <Select
              mode="multiple"
              value={data.configuration?.filter_appointment_type_ids ?? []}
              onChange={(v) => updateConfig({ filter_appointment_type_ids: v })}
              options={builderData.appointmentTypes.map((t) => ({ value: t.id, label: t.name }))}
              placeholder="All appointment types"
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              loading={builderData.dataLoading}
            />
          </Form.Item>
        )}

        {triggerEvent === 'form_completed' && (
          <Form.Item label="Specific Form" extra="Leave empty to trigger for any form">
            <Select
              value={data.configuration?.form_id}
              onChange={(v) => {
                const form = builderData.forms.find((f) => f.id === v);
                updateConfig({ form_id: v, form_name: form?.title });
              }}
              options={builderData.forms.map((f) => ({ value: f.id, label: f.title }))}
              placeholder="Any form"
              showSearch
              optionFilterProp="label"
              allowClear
              style={{ width: '100%' }}
              loading={builderData.dataLoading}
            />
          </Form.Item>
        )}
      </>
    );
  };

  const renderActionConfig = () => {
    const actions: any[] = data.configuration?.actions ?? [];
    const requiresInput = data.configuration?.requires_practitioner_input ?? false;

    // Which action types produce fillable fields (must match buildInputSchema on backend)
    const FILLABLE_TYPES = new Set(['assign_medication', 'create_test_order', 'cancel_appointment']);
    const hasFillableActions = actions.some((a) => FILLABLE_TYPES.has(a.action_type));

    return (
      <Form.Item label="Actions">
        <Space direction="vertical" style={{ width: '100%' }}>
          {/* ── Practitioner-input toggle ── */}
          {hasFillableActions && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 6,
                border: `1px solid ${requiresInput ? '#ffe58f' : '#e8e8e8'}`,
              }}
            >
              <Switch
                checked={requiresInput}
                onChange={(v) => updateConfig({ requires_practitioner_input: v })}
                size="small"
              />
              <div>
                <Text style={{ fontSize: 12, fontWeight: requiresInput ? 600 : 400 }}>
                  Require practitioner input before executing
                </Text>
                {requiresInput && (
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                    Stage will pause and prompt the practitioner to review and confirm field values (e.g. dosage) before the actions run.
                  </Text>
                )}
              </div>
            </div>
          )}

          {actions.map((action: any, i: number) => (
            <Card
              key={i}
              size="small"
              style={{ borderRadius: 6, border: '1px solid #e8e8e8' }}
              styles={{ body: { padding: 10 } }}
              title={
                <Text style={{ fontSize: 12, color: '#595959' }}>
                  {ACTION_TYPES.find((a) => a.value === action.action_type)?.label ?? `Action ${i + 1}`}
                </Text>
              }
              extra={
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    const updated = [...actions];
                    updated.splice(i, 1);
                    updateConfig({ actions: updated });
                  }}
                />
              }
            >
              <Select
                value={action.action_type || undefined}
                options={ACTION_TYPES}
                placeholder="Select action type"
                style={{ width: '100%', marginBottom: action.action_type ? 8 : 0 }}
                onChange={(v) => {
                  const updated = [...actions];
                  updated[i] = { action_type: v, config: {} };
                  updateConfig({ actions: updated });
                }}
              />
              {action.action_type && renderActionFields(action, i, actions)}
            </Card>
          ))}
          <Button
            icon={<PlusOutlined />}
            size="small"
            block
            onClick={() => updateConfig({ actions: [...actions, { action_type: '', config: {} }] })}
          >
            Add Action
          </Button>
        </Space>
      </Form.Item>
    );
  };

  const renderConditionConfig = () => {
    const conditionMode: string = data.configuration?.condition_type ?? 'rule_based';
    const conditionObj = data.configuration?.condition ?? { operator: 'and', conditions: [] };
    const conditions: any[] = conditionObj.conditions ?? [];

    const updateConditions = (newConditions: any[]) => {
      updateConfig({ condition: { ...conditionObj, conditions: newConditions } });
    };

    return (
      <>
        <Form.Item label="Condition Mode">
          <Segmented
            block
            value={conditionMode}
            onChange={(v) => updateConfig({ condition_type: v as string })}
            options={[
              { value: 'rule_based', label: 'Rule-Based' },
              { value: 'all_predecessors_complete', label: 'All Steps Complete' },
            ]}
          />
        </Form.Item>

        {conditionMode === 'all_predecessors_complete' ? (
          <Alert
            type="info"
            showIcon
            message="All Predecessors Complete"
            description={
              'This node waits until every stage that connects into it has a completed status, ' +
              'then automatically advances along the Yes path. ' +
              'Draw connections from each parallel step into this node, then connect its Yes output to the next stage.'
            }
            style={{ borderRadius: 8 }}
          />
        ) : (
          <>
            <Form.Item label="Match Rule">
              <Select
                value={conditionObj.operator ?? 'and'}
                onChange={(v) => updateConfig({ condition: { ...conditionObj, operator: v } })}
                options={CONDITION_OPERATORS}
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              label={
                <Space>
                  <span>Conditions</span>
                  <Badge count={conditions.length} size="small" style={{ backgroundColor: '#8b5cf6' }} />
                </Space>
              }
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                {conditions.map((cond: any, i: number) => (
                  <Card
                    key={i}
                    size="small"
                    style={{ borderRadius: 6, border: '1px solid #e8e8e8' }}
                    styles={{ body: { padding: 10 } }}
                    extra={
                      <Button
                        size="small"
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => updateConditions(conditions.filter((_, idx) => idx !== i))}
                      />
                    }
                  >
                    <Select
                      value={cond.type || undefined}
                      onChange={(v) => {
                        const updated = [...conditions];
                        updated[i] = { type: v };
                        updateConditions(updated);
                      }}
                      options={CONDITION_TYPES}
                      placeholder="Select condition type"
                      style={{ width: '100%', marginBottom: cond.type ? 8 : 0 }}
                    />
                    {cond.type && renderConditionFields(cond, i, conditions)}
                  </Card>
                ))}
                <Button
                  icon={<PlusOutlined />}
                  size="small"
                  block
                  onClick={() => updateConditions([...conditions, { type: '' }])}
                >
                  Add Condition
                </Button>
              </Space>
            </Form.Item>
          </>
        )}
      </>
    );
  };

  const renderTimeConditionConfig = () => (
    <>
      <Form.Item label="Wait Relative To">
        <Select
          value={data.configuration?.anchor}
          onChange={(v) => updateConfig({ anchor: v })}
          options={TIME_ANCHORS}
          placeholder="Select anchor point"
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item label="Direction">
        <Select
          value={data.configuration?.delay_direction ?? 'after'}
          onChange={(v) => updateConfig({ delay_direction: v })}
          options={[
            { value: 'after',  label: 'After' },
            { value: 'before', label: 'Before' },
          ]}
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item label="Offset">
        <Space>
          <InputNumber
            min={0}
            value={data.configuration?.offset_days ?? 0}
            onChange={(v) => updateConfig({ offset_days: v ?? 0 })}
            style={{ width: 90 }}
            addonAfter="days"
          />
          <InputNumber
            min={0}
            max={23}
            value={data.configuration?.offset_hours ?? 0}
            onChange={(v) => updateConfig({ offset_hours: v ?? 0 })}
            style={{ width: 90 }}
            addonAfter="hrs"
          />
        </Space>
      </Form.Item>
    </>
  );

  // ── Single watcher item config (reusable for both single and multi modes) ──
  const renderSingleWatcherItemFields = (
    cfg: Record<string, any>,
    onChange: (patch: Record<string, any>) => void,
  ) => {
    const wType = cfg.watcher_type;
    return (
      <Space direction="vertical" style={{ width: '100%' }} size={6}>
        <Select
          value={wType || undefined}
          onChange={(v) =>
            onChange({
              watcher_type:          v,
              form_id:               undefined,
              form_name:             undefined,
              appointment_type_id:   undefined,
              status_value:          undefined,
              medication_catalog_id: undefined,
              medication_name:       undefined,
              medication_category:   undefined,
            })
          }
          options={WATCHER_TYPES}
          placeholder="Select event to watch"
          style={{ width: '100%' }}
        />

        {wType === 'form_completed' && (
          <Select
            value={cfg.form_id}
            onChange={(v) => {
              const form = builderData.forms.find((f) => f.id === v);
              onChange({ form_id: v, form_name: form?.title });
            }}
            options={builderData.forms.map((f) => ({ value: f.id, label: f.title }))}
            placeholder="Any form"
            showSearch
            optionFilterProp="label"
            allowClear
            style={{ width: '100%' }}
            loading={builderData.dataLoading}
          />
        )}

        {(wType === 'appointment_booked' || wType === 'appointment_status') && (
          <Select
            value={cfg.appointment_type_id}
            onChange={(v) => onChange({ appointment_type_id: v })}
            options={builderData.appointmentTypes.map((t) => ({ value: t.id, label: t.name }))}
            placeholder="Any appointment type"
            showSearch
            optionFilterProp="label"
            allowClear
            style={{ width: '100%' }}
            loading={builderData.dataLoading}
          />
        )}

        {wType === 'appointment_status' && (
          <Select
            value={cfg.status_value}
            onChange={(v) => onChange({ status_value: v })}
            options={consultStatuses.map((s) => ({ value: s, label: s }))}
            placeholder="Select appointment status"
            style={{ width: '100%' }}
          />
        )}

        {wType === 'medication_script_uploaded' && (
          <Select
            value={cfg.medication_catalog_id ?? undefined}
            onChange={(v) => {
              const med = builderData.medicationCatalog.find((m) => m.id === v);
              onChange({
                medication_catalog_id: v ?? undefined,
                medication_name:       med?.name ?? undefined,
                medication_category:   med?.medication_category ?? undefined,
              });
            }}
            onClear={() => onChange({ medication_catalog_id: undefined, medication_name: undefined, medication_category: undefined })}
            options={builderData.medicationCatalog.map((m) => ({
              value: m.id,
              label: m.generic_name ? `${m.name} (${m.generic_name})` : m.name,
            }))}
            placeholder="Any medication"
            showSearch
            optionFilterProp="label"
            allowClear
            style={{ width: '100%' }}
            loading={builderData.dataLoading}
            notFoundContent={builderData.dataLoading ? <Spin size="small" /> : 'No catalog medications'}
          />
        )}
      </Space>
    );
  };

  const renderWatcherConfig = () => {
    const isMulti: boolean = !!(data.configuration?.watcher_items?.length || data.configuration?.watcher_mode === 'multi');
    const watcherItems: Array<{ item_key: string; label: string; watcher_type?: string; watch_config: Record<string, any> }> =
      data.configuration?.watcher_items ?? [];

    if (isMulti) {
      return (
        <>
          <div
            style={{
              padding: '8px 10px',
              borderRadius: 6,
              border: '1px solid #bae0ff',
              fontSize: 11,
              color: '#0958d9',
              marginBottom: 12,
            }}
          >
            All conditions must be met before the plan advances (AND logic).
          </div>

          <Form.Item
            label={
              <Space>
                <span>Watch Conditions</span>
                <Badge count={watcherItems.length} size="small" style={{ backgroundColor: '#06b6d4' }} />
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {watcherItems.map((item, i) => (
                <Card
                  key={item.item_key}
                  size="small"
                  style={{ borderRadius: 6, border: '1px solid #e8e8e8' }}
                  styles={{ body: { padding: 10 } }}
                  title={
                    <Input
                      value={item.label}
                      onChange={(e) => {
                        const updated = [...watcherItems];
                        updated[i] = { ...updated[i], label: e.target.value };
                        updateConfig({ watcher_items: updated });
                      }}
                      placeholder={`Condition ${i + 1} label`}
                      size="small"
                      style={{ fontWeight: 600, border: 'none', padding: 0, boxShadow: 'none' }}
                    />
                  }
                  extra={
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        const updated = [...watcherItems];
                        updated.splice(i, 1);
                        updateConfig({ watcher_items: updated, watcher_mode: updated.length > 0 ? 'multi' : undefined });
                      }}
                    />
                  }
                >
                  {renderSingleWatcherItemFields(
                    { watcher_type: item.watcher_type, ...item.watch_config },
                    (patch) => {
                      const updated = [...watcherItems];
                      const { watcher_type, ...watchCfg } = patch;
                      updated[i] = {
                        ...updated[i],
                        ...(watcher_type !== undefined ? { watcher_type } : {}),
                        watch_config: { ...updated[i].watch_config, ...watchCfg },
                      };
                      updateConfig({ watcher_items: updated });
                    },
                  )}
                </Card>
              ))}
              <Button
                icon={<PlusOutlined />}
                size="small"
                block
                onClick={() =>
                  updateConfig({
                    watcher_mode:  'multi',
                    watcher_items: [
                      ...watcherItems,
                      {
                        item_key:     `item_${Date.now()}`,
                        label:        `Condition ${watcherItems.length + 1}`,
                        watcher_type: undefined,
                        watch_config: {},
                      },
                    ],
                  })
                }
              >
                Add Condition
              </Button>
              <Button
                size="small"
                block
                onClick={() => updateConfig({ watcher_items: undefined, watcher_mode: undefined, watcher_type: undefined })}
              >
                Switch to Single Condition
              </Button>
            </Space>
          </Form.Item>
        </>
      );
    }

    // Single-condition mode
    const watcherType = data.configuration?.watcher_type;
    return (
      <>
        <Form.Item label="Watch For Event">
          {renderSingleWatcherItemFields(
            { watcher_type: watcherType, ...data.configuration },
            (patch) => {
              const { watcher_type, ...rest } = patch;
              updateConfig({ watcher_type: watcher_type ?? undefined, ...rest });
            },
          )}
        </Form.Item>

        {/* Medication category fallback */}
        {watcherType === 'medication_script_uploaded' && !data.configuration?.medication_catalog_id && (
          <Form.Item label="Match by Category" extra="Fires for any medication in this category.">
            <Select
              value={data.configuration?.medication_category ?? undefined}
              onChange={(v) => updateConfig({ medication_category: v ?? undefined })}
              options={MEDICATION_CATEGORIES}
              placeholder="Any category"
              allowClear
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        <Button
          icon={<PlusOutlined />}
          size="small"
          block
          style={{ marginTop: 8 }}
          onClick={() =>
            updateConfig({
              watcher_mode:  'multi',
              watcher_items: [
                {
                  item_key:     'item_1',
                  label:        'Condition 1',
                  watcher_type: watcherType,
                  watch_config: {},
                },
                {
                  item_key:     'item_2',
                  label:        'Condition 2',
                  watcher_type: undefined,
                  watch_config: {},
                },
              ],
              watcher_type: undefined,
            })
          }
        >
          Add Another Condition (AND logic)
        </Button>
      </>
    );
  };

  const renderManualConfig = () => (
    <Form.Item label="Practitioner Instructions">
      <TextArea
        rows={4}
        value={data.configuration?.instructions ?? ''}
        onChange={(e) => updateConfig({ instructions: e.target.value })}
        placeholder="Describe what the practitioner must do at this step..."
      />
    </Form.Item>
  );

  const renderSubPlanConfig = () => {
    const subPlans: Array<{ template_id: number; template_name: string }> =
      data.configuration?.sub_plans ?? [];
    const selectionMode: string = data.configuration?.selection_mode ?? 'practitioner_select';

    return (
      <>
        <Form.Item
          label="Selection Mode"
          extra={
            selectionMode === 'auto_all'
              ? 'All listed plans are automatically assigned when this stage is reached.'
              : 'The practitioner chooses which plan(s) to assign during consultation.'
          }
        >
          <Select
            value={selectionMode}
            onChange={(v) => updateConfig({ selection_mode: v })}
            options={[
              { value: 'practitioner_select', label: 'Practitioner Selects' },
              { value: 'auto_all',            label: 'Assign All Automatically' },
            ]}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item label="Available Plans">
          <Space direction="vertical" style={{ width: '100%' }}>
            {subPlans.map((sp, i) => (
              <Card
                key={i}
                size="small"
                style={{ borderRadius: 6, border: '1px solid #e8e8e8' }}
                styles={{ body: { padding: 10 } }}
                extra={
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      const updated = [...subPlans];
                      updated.splice(i, 1);
                      updateConfig({ sub_plans: updated });
                    }}
                  />
                }
              >
                <Select
                  value={sp.template_id ?? undefined}
                  onChange={(v) => {
                    const tpl = builderData.treatmentPlanTemplates.find((t) => t.id === v);
                    const updated = [...subPlans];
                    updated[i] = { template_id: v, template_name: tpl?.name ?? '' };
                    updateConfig({ sub_plans: updated });
                  }}
                  options={builderData.treatmentPlanTemplates.map((t) => ({
                    value: t.id,
                    label: t.name,
                  }))}
                  placeholder="Select a treatment plan template"
                  showSearch
                  optionFilterProp="label"
                  style={{ width: '100%' }}
                  loading={builderData.dataLoading}
                />
              </Card>
            ))}
            <Button
              icon={<PlusOutlined />}
              size="small"
              block
              onClick={() =>
                updateConfig({
                  sub_plans: [...subPlans, { template_id: 0, template_name: '' }],
                })
              }
            >
              Add Plan Option
            </Button>
          </Space>
        </Form.Item>
      </>
    );
  };

  const renderAppointmentConfig = () => (
    <>
      <Form.Item label="Appointment Type">
        <Select
          value={data.configuration?.appointment_type_id}
          onChange={(v) => {
            const at = builderData.appointmentTypes.find((a) => a.id === v);
            updateConfig({ appointment_type_id: v, appointment_type_name: at?.name });
          }}
          options={builderData.appointmentTypes.map((t) => ({ value: t.id, label: t.name }))}
          placeholder="Select appointment type"
          showSearch
          optionFilterProp="label"
          allowClear
          style={{ width: '100%' }}
          loading={builderData.dataLoading}
          notFoundContent={builderData.dataLoading ? <Spin size="small" /> : 'No types found'}
        />
      </Form.Item>
      <Form.Item label="Note Template">
        <Select
          value={data.configuration?.note_template_id}
          onChange={(v) => {
            const nt = builderData.noteTemplates.find((n) => n.id === v);
            updateConfig({ note_template_id: v, note_template_name: nt?.name });
          }}
          options={builderData.noteTemplates.map((t) => ({ value: t.id, label: t.name }))}
          placeholder="Optional note template"
          showSearch
          optionFilterProp="label"
          allowClear
          style={{ width: '100%' }}
          loading={builderData.dataLoading}
        />
      </Form.Item>
    </>
  );

  const renderPractitionerTaskConfig = () => {
    const taskType    = data.configuration?.task_type ?? '';
    const taskConfig  = data.configuration?.task_config ?? {};
    const updateTC    = (patch: Record<string, any>) =>
      updateConfig({ task_config: { ...taskConfig, ...patch } });

    return (
      <>
        <Form.Item label="Task Type">
          <Select
            value={taskType || undefined}
            onChange={(v) => updateConfig({ task_type: v, task_config: {} })}
            options={PRACTITIONER_TASK_TYPES}
            placeholder="Select task type"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item label="Task Title">
          <Input
            value={data.configuration?.task_title ?? ''}
            onChange={(e) => updateConfig({ task_title: e.target.value })}
            placeholder="Short title shown to the practitioner"
          />
        </Form.Item>

        <Form.Item label="Instructions">
          <TextArea
            value={data.configuration?.task_instructions ?? ''}
            onChange={(e) => updateConfig({ task_instructions: e.target.value })}
            placeholder="Optional instructions for the practitioner"
            rows={3}
          />
        </Form.Item>

        {taskType === 'upload_resource' && (
          <Form.Item label="Document Category">
            <Input
              value={taskConfig.document_category ?? ''}
              onChange={(e) => updateTC({ document_category: e.target.value })}
              placeholder="e.g. lab_results, referral_letter"
            />
          </Form.Item>
        )}

        {taskType === 'complete_practitioner_form' && (
          <Form.Item label="Form">
            <Select
              value={taskConfig.form_id ?? undefined}
              onChange={(v) => {
                const form = builderData.practitionerForms.find((f) => f.id === v);
                updateTC({ form_id: v, form_name: form?.title ?? '' });
              }}
              options={builderData.practitionerForms.map((f) => ({ value: f.id, label: f.title }))}
              placeholder="Select practitioner form"
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              loading={builderData.dataLoading}
              notFoundContent={builderData.dataLoading ? <Spin size="small" /> : 'No practitioner forms found'}
            />
          </Form.Item>
        )}

        {taskType === 'create_test_order' && (
          <>
            <Form.Item label="Default Priority">
              <Select
                value={taskConfig.priority ?? 'routine'}
                onChange={(v) => updateTC({ priority: v })}
                options={TEST_PRIORITIES}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="Default Notes">
              <Input
                value={taskConfig.notes ?? ''}
                onChange={(e) => updateTC({ notes: e.target.value })}
                placeholder="Optional notes pre-filled for the practitioner"
              />
            </Form.Item>
            <Form.Item label="Required Blood Tests">
              <Select
                mode="multiple"
                value={taskConfig.required_blood_tests ?? []}
                onChange={(v) => updateTC({ required_blood_tests: v })}
                options={testTypeOptions}
                placeholder="Select required test types"
                optionFilterProp="label"
                showSearch
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item label="Allowed Test Packages" extra="Limits which packages practitioners can order for this task">
              <Select
                mode="multiple"
                value={taskConfig.allowed_test_package_ids ?? []}
                onChange={(v) => updateTC({ allowed_test_package_ids: v })}
                options={builderData.customTestPackages.map((p) => ({ value: p.id, label: p.name }))}
                placeholder="All packages (no restriction)"
                showSearch
                optionFilterProp="label"
                allowClear
                style={{ width: '100%' }}
                loading={builderData.dataLoading}
              />
            </Form.Item>
          </>
        )}

        {taskType === 'record_vital_signs' && (
          <Form.Item label="Required Fields">
            <Select
              mode="multiple"
              value={taskConfig.required_fields ?? []}
              onChange={(v) => updateTC({ required_fields: v })}
              options={[
                { value: 'bp_systolic',      label: 'Blood Pressure (Systolic)' },
                { value: 'bp_diastolic',     label: 'Blood Pressure (Diastolic)' },
                { value: 'heart_rate',       label: 'Heart Rate' },
                { value: 'weight_kg',        label: 'Weight (kg)' },
                { value: 'height_cm',        label: 'Height (cm)' },
                { value: 'temperature_c',    label: 'Temperature (°C)' },
                { value: 'o2_saturation',    label: 'O₂ Saturation (%)' },
                { value: 'respiratory_rate', label: 'Respiratory Rate' },
                { value: 'pain',             label: 'Pain (0-10)' },
              ]}
              placeholder="Select required vital sign fields"
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        {taskType === 'complete_referral' && (
          <>
            <Form.Item label="Default Specialist Type">
              <Input
                value={taskConfig.specialist_type ?? ''}
                onChange={(e) => updateTC({ specialist_type: e.target.value })}
                placeholder="e.g. Cardiologist, Dermatologist"
              />
            </Form.Item>
            <Form.Item label="Default Urgency">
              <Select
                value={taskConfig.urgency ?? 'routine'}
                onChange={(v) => updateTC({ urgency: v })}
                options={TEST_PRIORITIES}
                style={{ width: '100%' }}
              />
            </Form.Item>
          </>
        )}

        {taskType === 'prescribe_medication' && (
          <>
            <Form.Item label="Default Medication">
              <Select
                value={taskConfig.medication_catalog_id ?? undefined}
                onChange={(v) => {
                  const med = builderData.medicationCatalog.find((m) => m.id === v);
                  updateTC({ medication_catalog_id: v, medication_name: med?.name ?? '' });
                }}
                options={builderData.medicationCatalog.map((m) => ({ value: m.id, label: m.name }))}
                placeholder="Optional — pre-selects a medication"
                showSearch
                optionFilterProp="label"
                allowClear
                style={{ width: '100%' }}
                loading={builderData.dataLoading}
              />
            </Form.Item>
            <Form.Item label="Require Script Upload">
              <Switch
                checked={taskConfig.require_script ?? false}
                onChange={(v) => updateTC({ require_script: v })}
                size="small"
              />
            </Form.Item>
          </>
        )}
      </>
    );
  };

  const renderPractitionerOptionConfig = () => {
    const options: Array<{ option_key: string; label: string; description?: string }> =
      data.configuration?.options ?? [];

    const slugify = (str: string) =>
      str.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `option_${Date.now()}`;

    const allKeys = options.map((o) => o.option_key);
    const hasDuplicateKeys = allKeys.length !== new Set(allKeys).size;

    return (
      <>
        <Form.Item label="Prompt">
          <Input
            value={data.configuration?.prompt ?? ''}
            onChange={(e) => updateConfig({ prompt: e.target.value })}
            placeholder="e.g. Which treatment path should we follow?"
          />
        </Form.Item>

        <Form.Item
          label={
            <Space>
              <span>Options</span>
              <Badge count={options.length} size="small" style={{ backgroundColor: '#dc2626' }} />
            </Space>
          }
          validateStatus={hasDuplicateKeys ? 'error' : undefined}
          help={hasDuplicateKeys ? 'Option keys must be unique.' : options.length < 2 ? 'At least 2 options required.' : undefined}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            {options.map((opt, i) => (
              <Card
                key={i}
                size="small"
                style={{ borderRadius: 6, border: '1px solid #e8e8e8' }}
                styles={{ body: { padding: 10 } }}
                extra={
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={options.length <= 2}
                    onClick={() => {
                      const updated = [...options];
                      updated.splice(i, 1);
                      updateConfig({ options: updated });
                    }}
                  />
                }
              >
                <Space direction="vertical" style={{ width: '100%' }} size={4}>
                  <Input
                    value={opt.label}
                    onChange={(e) => {
                      const updated = [...options];
                      const autoKey = slugify(e.target.value);
                      updated[i] = { ...updated[i], label: e.target.value, option_key: autoKey };
                      updateConfig({ options: updated });
                    }}
                    placeholder="Option label (e.g. Conservative Treatment)"
                    size="small"
                  />
                  <Input
                    value={opt.option_key}
                    onChange={(e) => {
                      const updated = [...options];
                      updated[i] = { ...updated[i], option_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') };
                      updateConfig({ options: updated });
                    }}
                    placeholder="option_key (auto-generated)"
                    size="small"
                    addonBefore="key"
                    style={{ fontFamily: 'monospace', fontSize: 11 }}
                  />
                  <Input
                    value={opt.description ?? ''}
                    onChange={(e) => {
                      const updated = [...options];
                      updated[i] = { ...updated[i], description: e.target.value };
                      updateConfig({ options: updated });
                    }}
                    placeholder="Optional description"
                    size="small"
                  />
                </Space>
              </Card>
            ))}
            <Button
              icon={<PlusOutlined />}
              size="small"
              block
              onClick={() => {
                const idx = options.length + 1;
                updateConfig({
                  options: [
                    ...options,
                    { option_key: `option_${idx}`, label: `Option ${idx}`, description: '' },
                  ],
                });
              }}
            >
              Add Option
            </Button>
          </Space>
        </Form.Item>

        <div style={{ fontSize: 11, color: 'var(--mg-text-secondary)', padding: '8px 10px', background: 'var(--mg-bg-subtle)', borderRadius: 6, border: '1px solid var(--mg-border)' }}>
          Each option appears as a separate handle on the node. Draw a connection from each handle to the next step in that path.
        </div>
      </>
    );
  };

  const renderComprehensiveAnalysisConfig = () => {
    const dataSources: string[] = data.configuration?.data_sources ?? [];

    return (
      <>
        <Form.Item label="Data Sources">
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            {COMPREHENSIVE_DATA_SOURCE_OPTIONS.map((opt) => (
              <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={dataSources.includes(opt.value)}
                  onChange={(e) => {
                    const updated = e.target.checked
                      ? [...dataSources, opt.value]
                      : dataSources.filter((s) => s !== opt.value);
                    updateConfig({ data_sources: updated });
                  }}
                />
                {opt.label}
              </label>
            ))}
          </Space>
        </Form.Item>

        <Form.Item label="Include Medication Recommendations">
          <Switch
            checked={data.configuration?.include_medication_recommendations ?? false}
            onChange={(v) => updateConfig({ include_medication_recommendations: v })}
            size="small"
          />
        </Form.Item>

        <Form.Item label="Requires Practitioner Review">
          <Space direction="vertical" size={4}>
            <Switch checked={true} disabled size="small" />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Practitioners must review before the plan advances.
            </Text>
          </Space>
        </Form.Item>
      </>
    );
  };

  const renderConfigSection = () => {
    switch (data.stage_type) {
      case 'trigger':                  return renderTriggerConfig();
      case 'action':                   return renderActionConfig();
      case 'condition':                return renderConditionConfig();
      case 'time_condition':           return renderTimeConditionConfig();
      case 'watcher':                  return renderWatcherConfig();
      case 'manual':                   return renderManualConfig();
      case 'sub_plan_selection':       return renderSubPlanConfig();
      case 'appointment':              return renderAppointmentConfig();
      case 'practitioner_task':        return renderPractitionerTaskConfig();
      case 'patient_analysis':         return renderPatientAnalysisConfig();
      case 'practitioner_option':      return renderPractitionerOptionConfig();
      case 'comprehensive_analysis':   return renderComprehensiveAnalysisConfig();
      default:                         return null;
    }
  };

  const renderPatientAnalysisConfig = () => {
    return (
      <>
        <Form.Item
          label="Guidance Workflow"
          extra="The AI workflow template used to generate the patient guidance."
        >
          <Select
            value={data.configuration?.workflow_template_id ?? undefined}
            onChange={(v) => {
              const tpl = builderData.workflowTemplates.find((t) => t.id === v);
              updateConfig({ workflow_template_id: v, workflow_name: tpl?.name ?? '' });
            }}
            options={builderData.workflowTemplates.map((t) => ({ value: t.id, label: t.name }))}
            placeholder="Select guidance workflow template"
            showSearch
            optionFilterProp="label"
            allowClear
            style={{ width: '100%' }}
            loading={builderData.dataLoading}
            notFoundContent={builderData.dataLoading ? null : 'No workflow templates found'}
          />
        </Form.Item>
        <div style={{ fontSize: 12, color: 'var(--mg-text-secondary)', padding: '8px 10px', background: 'var(--mg-bg-subtle)', borderRadius: 6, border: '1px solid var(--mg-border)' }}>
          When the plan reaches this node, AI guidance is automatically generated from the patient's profile. The plan pauses here until the practitioner reviews and confirms the guidance.
        </div>
      </>
    );
  };

  return (
    <div style={{ padding: '16px', height: '100%', overflowY: 'auto' }}>
      {/* Stage type header */}
      <div
        style={{
          display:       'flex',
          alignItems:    'center',
          gap:           8,
          marginBottom:  16,
          paddingBottom: 12,
          borderBottom:  '1px solid #f0f0f0',
        }}
      >
        <div
          style={{
            width:        10,
            height:       10,
            borderRadius: '50%',
            background:   cfg?.color ?? '#6b7280',
            flexShrink:   0,
          }}
        />
        <Text strong style={{ fontSize: 13 }}>
          {cfg?.label ?? data.stage_type}
        </Text>
        {builderData.dataLoading && (
          <Spin size="small" style={{ marginLeft: 'auto' }} />
        )}
      </div>

      <Form layout="vertical" size="small">
        <Form.Item label="Label">
          <Input
            value={data.label}
            onChange={(e) => update({ label: e.target.value })}
            placeholder="Node label"
          />
        </Form.Item>

        <Form.Item label="Description">
          <Input
            value={data.description ?? ''}
            onChange={(e) => update({ description: e.target.value })}
            placeholder="Optional description"
          />
        </Form.Item>

        <Form.Item label="Entry Point">
          <Switch
            checked={data.is_entry_point}
            onChange={(v) => update({ is_entry_point: v })}
            size="small"
          />
        </Form.Item>

        <Form.Item label="Required">
          <Switch
            checked={data.is_required ?? true}
            onChange={(v) => update({ is_required: v })}
            checkedChildren="Required"
            unCheckedChildren="Optional"
            size="small"
          />
        </Form.Item>

        <Divider style={{ margin: '12px 0' }} />

        {renderConfigSection()}

        <Divider style={{ margin: '12px 0' }} />

        <Button danger block size="small" onClick={() => onDelete(node.id)}>
          Remove Node
        </Button>
      </Form>
    </div>
  );
};

export default NodePropertiesPanel;